// ============================================
// Doc Module — Clinical Calibration
//
// 3-Phase blind diagnosis workflow:
//   Phase 1: Diagnose (review labs, chat, formulary)
//   Phase 2: Reveal (compare vs MedGemma 27B)
//   Phase 3: Tune (prompt maker for model fine-tuning)
//
// Design: Quality Platform light theme tokens
// No framer-motion. Inline styles only.
// ============================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { T, F, modules } from '../../components/quality/tokens';
import { ModuleShell } from '../../components/quality/ModuleShell';
import { STOCK_CASES } from '../../data/stockCases';
import type { StockCase } from '../../types/clinicalCalibration';

// ── Formulary Data ──

const FORMULARY = {
  supplements: [
    { id: 'thyroid-bundle', name: 'Thyroid Support Bundle', contents: 'Selenium 200mcg, Zinc 30mg, Ashwagandha 600mg, D3 5000IU' },
    { id: 'iron-support', name: 'Iron Support', contents: 'Ferrous bisglycinate 36mg + Vitamin C 500mg' },
    { id: 'b12-methyl', name: 'B12 Methylcobalamin', contents: 'Methylcobalamin 1000mcg sublingual' },
    { id: 'sleep-protocol', name: 'Sleep Protocol', contents: 'Magnesium glycinate 400mg, L-theanine 200mg' },
    { id: 'adrenal-support', name: 'Adrenal Support', contents: 'Rhodiola 300mg, Phosphatidylserine 100mg' },
    { id: 'omega-3', name: 'Omega-3 Ultra', contents: 'EPA 1000mg, DHA 500mg' },
    { id: 'probiotic', name: 'Gut Restore', contents: '50B CFU multi-strain probiotic + prebiotic' },
  ],
  rx: [
    { id: 'levo-low', name: 'Levothyroxine (Low-Dose)', contents: '25-50mcg daily \u2014 requires provider' },
    { id: 'levo-standard', name: 'Levothyroxine (Standard)', contents: '75-125mcg daily \u2014 requires provider' },
    { id: 'trt-topical', name: 'Testosterone (Topical)', contents: 'Compounded cream \u2014 requires provider' },
    { id: 'trt-inject', name: 'Testosterone (Injectable)', contents: 'Cypionate 100-200mg/wk \u2014 requires provider' },
    { id: 'progesterone', name: 'Progesterone (Oral)', contents: '100-200mg cyclic \u2014 requires provider' },
    { id: 'dhea', name: 'DHEA (Compounded)', contents: '5-25mg daily \u2014 requires provider' },
  ],
  longevity: [
    { id: 'nad-iv', name: 'NAD+ IV Protocol', contents: '250-500mg IV infusion, monthly' },
    { id: 'peptide-bpc', name: 'BPC-157 Protocol', contents: '250-500mcg daily, 4-week cycle' },
    { id: 'metformin-longevity', name: 'Metformin (Off-Label)', contents: '500mg daily \u2014 longevity dosing' },
    { id: 'rapamycin', name: 'Rapamycin (Cyclic)', contents: '5mg weekly \u2014 requires specialist' },
  ],
};

const LAB_PANELS = {
  thyroid: [
    { id: 'tsh-order', name: 'TSH' },
    { id: 'ft4-order', name: 'Free T4' },
    { id: 'ft3-order', name: 'Free T3' },
    { id: 'tpo-ab', name: 'TPO Antibodies' },
    { id: 'tg-ab', name: 'Thyroglobulin Ab' },
    { id: 'rt3', name: 'Reverse T3' },
  ],
  hormones: [
    { id: 'estradiol-order', name: 'Estradiol' },
    { id: 'progesterone-order', name: 'Progesterone' },
    { id: 'total-t', name: 'Total Testosterone' },
    { id: 'free-t', name: 'Free Testosterone' },
    { id: 'dhea-s', name: 'DHEA-S' },
    { id: 'shbg', name: 'SHBG' },
    { id: 'cortisol-am', name: 'Cortisol (AM)' },
  ],
  metabolic: [
    { id: 'cbc', name: 'CBC w/ Differential' },
    { id: 'cmp', name: 'CMP' },
    { id: 'ferritin-order', name: 'Ferritin' },
    { id: 'vit-d-order', name: 'Vitamin D 25-OH' },
    { id: 'b12-order', name: 'B12 (Serum)' },
    { id: 'folate', name: 'Folate' },
    { id: 'hba1c', name: 'HbA1c' },
    { id: 'hs-crp', name: 'hs-CRP' },
    { id: 'homocysteine', name: 'Homocysteine' },
  ],
  lipids: [
    { id: 'lipid-panel', name: 'Lipid Panel' },
    { id: 'apob', name: 'ApoB' },
    { id: 'lpa', name: 'Lp(a)' },
    { id: 'ox-ldl', name: 'Oxidized LDL' },
  ],
};

// ── Mock MedGemma data (Phase 2, stock-001) ──

const MOCK_MEDGEMMA = {
  diagnosis: 'Functional hypothyroidism with concurrent vitamin D deficiency and iron depletion. T4\u2192T3 conversion deficit suggested by low Free T3 despite borderline TSH. B12 suboptimal at 310 vs functional optimal 500-800.',
  protocol: 'Tier 1: Thyroid Support Bundle ($89/mo) + Iron Support ($29/mo) + B12 Methylcobalamin ($19/mo). Retest 8 weeks. Escalation: TSH >2.5 or persistent symptoms \u2192 provider consult ($49) \u2192 low-dose levo.',
  grade: 'B+' as const,
};

const MOCK_DECISION_POINTS = [
  { area: 'Primary Dx', drCPosition: '', medgemmaPosition: 'Functional hypothyroidism + D deficiency + iron depletion', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.20 },
  { area: 'Key Lab Read', drCPosition: '', medgemmaPosition: 'Elevated vs functional optimal 1.0-2.0', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.15 },
  { area: 'Secondary Findings', drCPosition: '', medgemmaPosition: 'Low Free T3 \u2014 T4\u2192T3 conversion deficit', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.10 },
  { area: 'Supplement-First', drCPosition: '', medgemmaPosition: 'Yes \u2014 Tier 1 before Rx', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.15 },
  { area: 'Product Selection', drCPosition: '', medgemmaPosition: 'Thyroid Bundle + Iron + B12', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.10 },
  { area: 'Escalation Path', drCPosition: '', medgemmaPosition: 'TSH >2.5 at 8 weeks \u2192 Gate 2', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.10 },
  { area: 'Missed Signals', drCPosition: '', medgemmaPosition: 'Not recommended (missed insomnia signal)', alignment: 'both_missed' as const, deltaNote: 'Patient reported insomnia \u2014 Sleep Protocol ($69/mo) not recommended by either', clinicalWeight: 0.08 },
  { area: 'Cost Awareness', drCPosition: '', medgemmaPosition: '$137/mo (Thyroid + Iron + B12)', alignment: 'delta' as const, deltaNote: 'MedGemma total $137/mo with B12 add-on', clinicalWeight: 0.07 },
  { area: 'Evidence Grade', drCPosition: '', medgemmaPosition: 'B+', alignment: 'aligned' as const, deltaNote: '', clinicalWeight: 0.05 },
];

// ── Mock Prompt Maker messages (Phase 3) ──

const MOCK_PROMPT_MSGS: { role: 'system' | 'ai' | 'user'; text: string }[] = [
  { role: 'system', text: 'Comparison complete. 7/9 decision points aligned. 2 divergences detected.' },
  { role: 'ai', text: 'Two interesting deltas on this case:\n\n1. B12 \u2014 MedGemma flagged 310 as suboptimal (functional optimal 500-800) and added methylcobalamin. You didn\'t mention B12. Should MedGemma continue flagging B12 below 500, or is 310 acceptable in your clinical judgment?\n\n2. Sleep Protocol \u2014 Patient mentioned insomnia ("wired but tired") and neither you nor MedGemma recommended the Sleep Protocol. Should this be a standard add-on when insomnia is reported alongside thyroid symptoms?' },
];

// ── Quick action chips for Phase 3 ──

const QUICK_ACTIONS = [
  'Keep flagging B12 below 500',
  'Add Sleep Protocol for insomnia',
  'Lower the B12 threshold to 400',
  'Ask patient about sleep quality first',
];

// ── Helpers ──

function isFlaggedLab(status: string): boolean {
  return status !== 'optimal';
}

function alignmentColor(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return T.success;
  if (alignment === 'delta') return T.danger;
  return '#D97706'; // amber
}

function alignmentBg(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return T.successSoft;
  if (alignment === 'delta') return T.dangerSoft;
  return T.warningSoft;
}

function alignmentLabel(alignment: 'aligned' | 'delta' | 'both_missed'): string {
  if (alignment === 'aligned') return 'Aligned';
  if (alignment === 'delta') return 'Delta';
  return 'Both Missed';
}

// ── Component ──

export function DocModule() {
  // ── State ──
  const [activeCase, setActiveCase] = useState('stock-001');
  const [caseOpen, setCaseOpen] = useState(false);
  const [phase, setPhase] = useState<'diagnose' | 'reveal' | 'tune'>('diagnose');
  const [inputTab, setInputTab] = useState<'labs' | 'transcript' | 'formulary'>('labs');
  const [formularyGroup, setFormularyGroup] = useState<'supplements' | 'rx' | 'longevity' | 'laborders'>('supplements');
  const [labOrderGroup, setLabOrderGroup] = useState<'thyroid' | 'hormones' | 'metabolic' | 'lipids'>('thyroid');
  const [flaggedLabs, setFlaggedLabs] = useState<Set<string>>(new Set());
  const [chatNotes, setChatNotes] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedLabOrders, setSelectedLabOrders] = useState<Set<string>>(new Set());
  const [diagnosis, setDiagnosis] = useState('');
  const [inputMode, setInputMode] = useState<'type' | 'voice'>('type');
  const [isRecording, setIsRecording] = useState(false);
  const [shelfOpen, setShelfOpen] = useState(false);
  const [generated, setGenerated] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tuneInput, setTuneInput] = useState('');
  const [tuneMessages, setTuneMessages] = useState(MOCK_PROMPT_MSGS);
  const lastScrollY = useRef(0);
  const shelfOpenRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Keep shelfOpenRef in sync
  useEffect(() => {
    shelfOpenRef.current = shelfOpen;
  }, [shelfOpen]);

  // ── Derived ──
  const currentCase: StockCase | undefined = STOCK_CASES.find(c => c.caseId === activeCase);
  const flaggedCount = currentCase ? currentCase.labs.filter(l => isFlaggedLab(l.status)).length : 0;

  const productCount = selectedProducts.size + selectedLabOrders.size;

  // Total shelf items
  const totalShelfItems = flaggedLabs.size + chatNotes.size + selectedProducts.size + selectedLabOrders.size;
  const canSubmit = diagnosis.trim().length > 0 || totalShelfItems > 0;

  // ── Reset evidence state ──
  const resetEvidence = useCallback(() => {
    setFlaggedLabs(new Set());
    setChatNotes(new Set());
    setSelectedProducts(new Set());
    setSelectedLabOrders(new Set());
    setDiagnosis('');
    setInputMode('type');
    setIsRecording(false);
    setShelfOpen(false);
    setInputTab('labs');
    setFormularyGroup('supplements');
    setLabOrderGroup('thyroid');
    setPhase('diagnose');
    setTuneInput('');
    setTuneMessages(MOCK_PROMPT_MSGS);
  }, []);

  // ── Case loading ──
  const handleLoadCase = useCallback((caseId: string) => {
    if (caseId === 'random') {
      setGenerating(true);
      setGenerated(false);
      setTimeout(() => {
        setActiveCase('stock-001');
        setGenerated(true);
        setGenerating(false);
        resetEvidence();
      }, 1500);
    } else {
      setActiveCase(caseId);
      setGenerated(true);
      setGenerating(false);
      resetEvidence();
    }
    setCaseOpen(false);
  }, [resetEvidence]);

  // ── Scroll handler for evidence shelf ──
  const handleContentScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const y = e.currentTarget.scrollTop;
    const delta = y - lastScrollY.current;
    if (delta > 12 && shelfOpenRef.current) {
      setShelfOpen(false);
    } else if (delta < -12 && !shelfOpenRef.current) {
      setShelfOpen(true);
    }
    lastScrollY.current = y;
  }, []);

  // ── Toggle helpers ──
  const toggleFlaggedLab = useCallback((marker: string) => {
    setFlaggedLabs(prev => {
      const next = new Set(prev);
      if (next.has(marker)) next.delete(marker);
      else next.add(marker);
      return next;
    });
  }, []);

  const toggleChatNote = useCallback((tag: string) => {
    setChatNotes(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }, []);

  const toggleProduct = useCallback((id: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleLabOrder = useCallback((id: string) => {
    setSelectedLabOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(() => {
    setPhase('reveal');
  }, []);

  // ── Tune send ──
  const handleTuneSend = useCallback(() => {
    if (!tuneInput.trim()) return;
    const newMsgs = [
      ...tuneMessages,
      { role: 'user' as const, text: tuneInput },
      { role: 'ai' as const, text: 'Got it. I\'ll update the prompt template to incorporate your feedback. This will be applied to the next calibration run.' },
    ];
    setTuneMessages(newMsgs);
    setTuneInput('');
  }, [tuneInput, tuneMessages]);

  // ── Lookup helpers for shelf chip labels ──
  const getLabName = useCallback((marker: string) => marker, []);

  const getProductName = useCallback((id: string) => {
    for (const items of [FORMULARY.supplements, FORMULARY.rx, FORMULARY.longevity]) {
      const found = items.find(p => p.id === id);
      if (found) return found.name;
    }
    return id;
  }, []);

  const getLabOrderName = useCallback((id: string) => {
    for (const panels of Object.values(LAB_PANELS)) {
      const found = panels.find(p => p.id === id);
      if (found) return found.name;
    }
    return id;
  }, []);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <ModuleShell>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}>
        {/* ── CUSTOM COMPACT HEADER ── */}
        <div style={{
          padding: '10px 16px 8px',
          background: T.bgCard,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Back arrow */}
            <button
              onClick={() => window.history.back()}
              style={{
                background: 'none', border: 'none', fontSize: 18,
                cursor: 'pointer', padding: 0, color: T.text,
                lineHeight: 1,
              }}
            >
              &#8592;
            </button>

            {/* Module badge */}
            <div style={{
              width: 28, height: 28, borderRadius: T.radius.sm,
              background: modules.doc.soft,
              border: `1.5px solid ${modules.doc.color}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: F.mono, fontSize: 10, fontWeight: 700,
              color: modules.doc.color, letterSpacing: 0.5,
              flexShrink: 0,
            }}>
              {modules.doc.abbr}
            </div>

            {/* Title + subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F.body, fontSize: T.fontSize.base, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
                Clinical Calibration
              </div>
            </div>

            {/* Case dropdown */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                onClick={() => setCaseOpen(!caseOpen)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px',
                  borderRadius: T.radius.sm,
                  border: `1px solid ${T.border}`,
                  background: T.bgInput,
                  fontFamily: F.body, fontSize: T.fontSize.sm,
                  color: T.textSoft, cursor: 'pointer',
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {currentCase ? currentCase.label.split(' \u2014 ')[0] : 'Select Case'}
                </span>
                <span style={{ fontSize: 10, lineHeight: 1 }}>{caseOpen ? '\u25B2' : '\u25BC'}</span>
              </button>

              {/* Dropdown menu */}
              {caseOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0,
                  marginTop: 4, width: 220,
                  background: T.bgCard, border: `1px solid ${T.border}`,
                  borderRadius: T.radius.md, boxShadow: T.shadowMd,
                  zIndex: 100, overflow: 'hidden',
                }}>
                  {/* Random */}
                  <div style={{
                    padding: '6px 12px', fontSize: 10, fontWeight: 700,
                    color: T.textMuted, textTransform: 'uppercase',
                    letterSpacing: 1, fontFamily: F.mono,
                    borderBottom: `1px solid ${T.borderSoft}`,
                  }}>
                    Random
                  </div>
                  <button
                    onClick={() => handleLoadCase('random')}
                    style={{
                      width: '100%', padding: '8px 12px', textAlign: 'left',
                      background: 'none', border: 'none', cursor: 'pointer',
                      fontFamily: F.body, fontSize: T.fontSize.sm, color: T.text,
                      borderBottom: `1px solid ${T.borderSoft}`,
                    }}
                  >
                    Generate Random Case
                  </button>

                  {/* Stock */}
                  <div style={{
                    padding: '6px 12px', fontSize: 10, fontWeight: 700,
                    color: T.textMuted, textTransform: 'uppercase',
                    letterSpacing: 1, fontFamily: F.mono,
                    borderBottom: `1px solid ${T.borderSoft}`,
                  }}>
                    Stock Cases
                  </div>
                  {STOCK_CASES.map(sc => (
                    <button
                      key={sc.caseId}
                      onClick={() => handleLoadCase(sc.caseId)}
                      style={{
                        width: '100%', padding: '8px 12px', textAlign: 'left',
                        background: sc.caseId === activeCase ? T.bgSoft : 'none',
                        border: 'none', cursor: 'pointer',
                        fontFamily: F.body, fontSize: T.fontSize.sm, color: T.text,
                        borderBottom: `1px solid ${T.borderSoft}`,
                      }}
                    >
                      {sc.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Play button */}
            <button
              onClick={() => {
                if (!generating) handleLoadCase(activeCase);
              }}
              style={{
                width: 32, height: 32, borderRadius: T.radius.full,
                background: generating ? T.accent : (generated ? T.info : T.bgInput),
                border: 'none', cursor: generating ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFFFFF', fontSize: 14,
                flexShrink: 0,
              }}
            >
              {generating ? (
                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>&#8635;</span>
              ) : (
                '\u25B6'
              )}
            </button>
          </div>

          {/* Phase tabs (only after submit) */}
          {phase !== 'diagnose' && (
            <div style={{
              display: 'flex', gap: 0, marginTop: 8,
            }}>
              <button
                onClick={() => setPhase('reveal')}
                style={{
                  flex: 1, padding: '6px 0',
                  fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                  border: `1px solid ${T.border}`,
                  borderRadius: `${T.radius.sm}px 0 0 ${T.radius.sm}px`,
                  background: phase === 'reveal' ? T.info : T.bgCard,
                  color: phase === 'reveal' ? '#FFFFFF' : T.textMuted,
                  cursor: 'pointer',
                }}
              >
                Compare
              </button>
              <button
                onClick={() => setPhase('tune')}
                style={{
                  flex: 1, padding: '6px 0',
                  fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                  border: `1px solid ${T.border}`,
                  borderLeft: 'none',
                  borderRadius: `0 ${T.radius.sm}px ${T.radius.sm}px 0`,
                  background: phase === 'tune' ? T.accent : T.bgCard,
                  color: phase === 'tune' ? '#FFFFFF' : T.textMuted,
                  cursor: 'pointer',
                }}
              >
                Tune
              </button>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* PHASE 1: BLIND DIAGNOSIS                     */}
        {/* ============================================ */}
        {phase === 'diagnose' && (
          <>
            {/* No case generated yet */}
            {!generated || !currentCase ? (
              <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: 40, textAlign: 'center',
              }}>
                {generating ? (
                  <>
                    <div style={{
                      width: 56, height: 56, borderRadius: T.radius.full,
                      background: T.accentSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 16,
                    }}>
                      <span style={{ fontSize: 24, animation: 'spin 1s linear infinite', display: 'inline-block' }}>&#8635;</span>
                    </div>
                    <div style={{ fontSize: T.fontSize.base, fontWeight: 600, color: T.text }}>
                      Generating case...
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{
                      width: 56, height: 56, borderRadius: T.radius.full,
                      background: T.bgSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 16,
                      fontSize: 24, color: T.textMuted,
                    }}>
                      &#128203;
                    </div>
                    <div style={{ fontSize: T.fontSize.base, fontWeight: 600, color: T.textSoft }}>
                      Tap play to load
                    </div>
                    <div style={{ fontSize: T.fontSize.sm, color: T.textMuted, marginTop: 4 }}>
                      Select a case from the dropdown, then press play
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* ── Tab Bar ── */}
                <div style={{
                  display: 'flex', flexShrink: 0,
                  margin: '8px 16px 0',
                }}>
                  {([
                    { key: 'labs' as const, label: `Labs (${flaggedLabs.size || flaggedCount})` },
                    { key: 'transcript' as const, label: `Chat (${chatNotes.size})` },
                    { key: 'formulary' as const, label: `Rx (${productCount})` },
                  ]).map((tab, i, arr) => (
                    <button
                      key={tab.key}
                      onClick={() => setInputTab(tab.key)}
                      style={{
                        flex: 1, padding: '8px 0',
                        fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                        border: `1px solid ${T.border}`,
                        borderLeft: i === 0 ? `1px solid ${T.border}` : 'none',
                        borderRadius: i === 0
                          ? `${T.radius.sm}px 0 0 ${T.radius.sm}px`
                          : i === arr.length - 1
                            ? `0 ${T.radius.sm}px ${T.radius.sm}px 0`
                            : '0',
                        background: inputTab === tab.key
                          ? (tab.key === 'formulary' && productCount > 0 ? T.success : T.info)
                          : T.bgCard,
                        color: inputTab === tab.key ? '#FFFFFF' : T.textMuted,
                        cursor: 'pointer',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* ── Evidence Shelf ── */}
                <div style={{ flexShrink: 0, padding: '0 16px' }}>
                  {/* Handle bar */}
                  <div
                    onClick={() => setShelfOpen(prev => !prev)}
                    style={{
                      display: 'flex', justifyContent: 'center',
                      padding: '6px 0', cursor: 'pointer',
                    }}
                  >
                    <div style={{
                      width: 32, height: 3,
                      borderRadius: T.radius.full,
                      background: T.borderStrong,
                    }} />
                  </div>

                  {/* Shelf chips */}
                  <div style={{
                    maxHeight: shelfOpen ? 200 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.25s ease',
                  }}>
                    {totalShelfItems === 0 ? (
                      <div style={{
                        fontSize: T.fontSize.sm, color: T.textGhost,
                        textAlign: 'center', padding: '8px 0',
                      }}>
                        No evidence collected yet
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 6,
                        paddingBottom: 8,
                      }}>
                        {/* Flagged labs — red chips */}
                        {Array.from(flaggedLabs).map(marker => (
                          <span
                            key={`fl-${marker}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: T.radius.full,
                              background: T.dangerSoft, color: T.danger,
                              fontSize: T.fontSize.sm, fontFamily: F.body,
                            }}
                          >
                            {getLabName(marker)}
                            <span
                              onClick={(e) => { e.stopPropagation(); toggleFlaggedLab(marker); }}
                              style={{ cursor: 'pointer', fontWeight: 700 }}
                            >
                              &times;
                            </span>
                          </span>
                        ))}

                        {/* Chat notes — amber chips */}
                        {Array.from(chatNotes).map(tag => (
                          <span
                            key={`cn-${tag}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: T.radius.full,
                              background: T.warningSoft, color: '#D97706',
                              fontSize: T.fontSize.sm, fontFamily: F.body,
                            }}
                          >
                            {tag}
                            <span
                              onClick={(e) => { e.stopPropagation(); toggleChatNote(tag); }}
                              style={{ cursor: 'pointer', fontWeight: 700 }}
                            >
                              &times;
                            </span>
                          </span>
                        ))}

                        {/* Products — green chips */}
                        {Array.from(selectedProducts).map(id => (
                          <span
                            key={`prod-${id}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: T.radius.full,
                              background: T.successSoft, color: T.success,
                              fontSize: T.fontSize.sm, fontFamily: F.body,
                            }}
                          >
                            {getProductName(id)}
                          </span>
                        ))}

                        {/* Lab orders — green chips with clipboard */}
                        {Array.from(selectedLabOrders).map(id => (
                          <span
                            key={`lo-${id}`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 8px', borderRadius: T.radius.full,
                              background: T.successSoft, color: T.success,
                              fontSize: T.fontSize.sm, fontFamily: F.body,
                            }}
                          >
                            &#128203; {getLabOrderName(id)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Formulary Sub-tabs (only when formulary tab active) ── */}
                {inputTab === 'formulary' && (
                  <div style={{
                    display: 'flex', gap: 6, padding: '0 16px 4px',
                    flexShrink: 0,
                  }}>
                    {([
                      { key: 'supplements' as const, label: 'Sups' },
                      { key: 'rx' as const, label: 'Rx' },
                      { key: 'longevity' as const, label: 'Long' },
                      { key: 'laborders' as const, label: 'Labs' },
                    ]).map(sub => (
                      <button
                        key={sub.key}
                        onClick={() => setFormularyGroup(sub.key)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: T.radius.full,
                          border: `1px solid ${formularyGroup === sub.key ? T.info : T.border}`,
                          background: formularyGroup === sub.key ? T.infoSoft : T.bgCard,
                          color: formularyGroup === sub.key ? T.info : T.textMuted,
                          fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* ── Lab order sub-group tabs (only when formulary > laborders) ── */}
                {inputTab === 'formulary' && formularyGroup === 'laborders' && (
                  <div style={{
                    display: 'flex', gap: 4, padding: '0 16px 4px',
                    flexShrink: 0, flexWrap: 'wrap',
                  }}>
                    {([
                      { key: 'thyroid' as const, label: 'Thyroid' },
                      { key: 'hormones' as const, label: 'Hormone' },
                      { key: 'metabolic' as const, label: 'Metabolic' },
                      { key: 'lipids' as const, label: 'Lipid' },
                    ]).map(sub => (
                      <button
                        key={sub.key}
                        onClick={() => setLabOrderGroup(sub.key)}
                        style={{
                          padding: '3px 8px',
                          borderRadius: T.radius.sm,
                          border: `1px solid ${labOrderGroup === sub.key ? T.success : T.borderSoft}`,
                          background: labOrderGroup === sub.key ? T.successSoft : T.bgSoft,
                          color: labOrderGroup === sub.key ? T.success : T.textMuted,
                          fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* ── Content Area (scrollable) ── */}
                <div
                  ref={contentRef}
                  onScroll={handleContentScroll}
                  style={{
                    flex: 1, overflowY: 'auto', overflowX: 'hidden',
                    padding: '0 16px 140px',
                  }}
                >
                  {/* ── LABS TAB ── */}
                  {inputTab === 'labs' && currentCase && (
                    <div style={{ paddingTop: 4 }}>
                      {currentCase.labs.map((lab, i) => {
                        const flagged = isFlaggedLab(lab.status);
                        const userFlagged = flaggedLabs.has(lab.biomarker);
                        return (
                          <div
                            key={lab.biomarker}
                            onClick={() => toggleFlaggedLab(lab.biomarker)}
                            style={{
                              display: 'flex', alignItems: 'center',
                              padding: '10px 12px',
                              background: i % 2 === 0 ? T.bgCard : T.bgSoft,
                              borderLeft: userFlagged ? `2px solid ${T.danger}` : '2px solid transparent',
                              cursor: 'pointer',
                              borderBottom: `1px solid ${T.borderSoft}`,
                              borderRadius: T.radius.sm,
                              marginBottom: 2,
                            }}
                          >
                            {/* Marker name */}
                            <div style={{
                              flex: 1, minWidth: 0,
                              fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                              color: T.text,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {lab.biomarker}
                            </div>
                            {/* Value */}
                            <div style={{
                              fontFamily: F.mono, fontSize: T.fontSize.sm,
                              fontWeight: 700,
                              color: flagged ? T.danger : T.text,
                              marginRight: 6, flexShrink: 0,
                            }}>
                              {lab.value}
                            </div>
                            {/* Unit */}
                            <div style={{
                              fontFamily: F.body, fontSize: T.fontSize.sm,
                              color: T.textMuted, marginRight: 8, flexShrink: 0,
                              minWidth: 40,
                            }}>
                              {lab.unit}
                            </div>
                            {/* Standard range */}
                            <div style={{
                              fontFamily: F.mono, fontSize: T.fontSize.sm,
                              color: T.textGhost, flexShrink: 0,
                            }}>
                              {lab.conventionalRange}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── CHAT TAB ── */}
                  {inputTab === 'transcript' && currentCase && (
                    <div style={{ paddingTop: 4 }}>
                      {currentCase.transcript.map((msg, i) => {
                        const isPatient = msg.role === 'patient';
                        return (
                          <div
                            key={i}
                            style={{
                              padding: '10px 12px',
                              borderLeft: `3px solid ${isPatient ? T.success : T.accent}`,
                              marginBottom: 8,
                              background: T.bgCard,
                              borderRadius: `0 ${T.radius.sm}px ${T.radius.sm}px 0`,
                            }}
                          >
                            {/* Role label */}
                            <div style={{
                              fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                              color: isPatient ? T.success : T.accent,
                              letterSpacing: 1, textTransform: 'uppercase',
                              marginBottom: 4,
                            }}>
                              {isPatient ? 'PATIENT' : 'AI NURSE'}
                            </div>
                            {/* Message text */}
                            <div style={{
                              fontFamily: F.body, fontSize: T.fontSize.sm,
                              color: T.text, lineHeight: 1.5,
                            }}>
                              {msg.text}
                            </div>
                            {/* Tags (patient messages only) */}
                            {isPatient && msg.tags.length > 0 && (
                              <div style={{
                                display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6,
                              }}>
                                {msg.tags.map(tag => {
                                  const active = chatNotes.has(tag);
                                  return (
                                    <button
                                      key={tag}
                                      onClick={() => toggleChatNote(tag)}
                                      style={{
                                        padding: '2px 8px',
                                        borderRadius: T.radius.full,
                                        border: `1px solid ${active ? '#D97706' : T.border}`,
                                        background: active ? T.warningSoft : T.bgSoft,
                                        color: active ? '#D97706' : T.textMuted,
                                        fontFamily: F.body, fontSize: T.fontSize.sm,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {active ? '\u2713 ' : ''}{tag}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── FORMULARY TAB ── */}
                  {inputTab === 'formulary' && (
                    <div style={{ paddingTop: 4 }}>
                      {/* Supplements / Rx / Longevity */}
                      {formularyGroup !== 'laborders' && (
                        <>
                          {FORMULARY[formularyGroup].map(item => {
                            const selected = selectedProducts.has(item.id);
                            return (
                              <div
                                key={item.id}
                                onClick={() => toggleProduct(item.id)}
                                style={{
                                  display: 'flex', alignItems: 'flex-start', gap: 10,
                                  padding: '10px 12px',
                                  background: T.bgCard,
                                  borderBottom: `1px solid ${T.borderSoft}`,
                                  cursor: 'pointer',
                                  borderRadius: T.radius.sm,
                                  marginBottom: 2,
                                }}
                              >
                                {/* Checkbox */}
                                <div style={{
                                  width: 20, height: 20, borderRadius: 4,
                                  border: `2px solid ${selected ? T.success : T.border}`,
                                  background: selected ? T.success : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  flexShrink: 0, marginTop: 1,
                                  color: '#FFFFFF', fontSize: 12, fontWeight: 700,
                                }}>
                                  {selected && '\u2713'}
                                </div>
                                {/* Product info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                                    color: selected ? T.success : T.text,
                                  }}>
                                    {item.name}
                                  </div>
                                  <div style={{
                                    fontFamily: F.body, fontSize: T.fontSize.sm,
                                    color: T.textMuted, marginTop: 2,
                                  }}>
                                    {item.contents}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Rx gate warning */}
                          {formularyGroup === 'rx' && (
                            <div style={{
                              margin: '8px 0', padding: '8px 12px',
                              borderRadius: T.radius.sm,
                              background: T.warningSoft,
                              border: `1px solid rgba(217,119,6,0.15)`,
                              display: 'flex', alignItems: 'center', gap: 6,
                            }}>
                              <span style={{ color: '#D97706', fontWeight: 700, fontSize: T.fontSize.sm }}>
                                Gate 2
                              </span>
                              <span style={{ fontSize: T.fontSize.sm, color: T.textMuted }}>
                                All Rx items require provider authorization
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Lab Orders */}
                      {formularyGroup === 'laborders' && (
                        <>
                          {LAB_PANELS[labOrderGroup].map(panel => {
                            const selected = selectedLabOrders.has(panel.id);
                            return (
                              <div
                                key={panel.id}
                                onClick={() => toggleLabOrder(panel.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  padding: '10px 12px',
                                  background: T.bgCard,
                                  borderBottom: `1px solid ${T.borderSoft}`,
                                  cursor: 'pointer',
                                  borderRadius: T.radius.sm,
                                  marginBottom: 2,
                                }}
                              >
                                <div style={{
                                  width: 20, height: 20, borderRadius: 4,
                                  border: `2px solid ${selected ? T.success : T.border}`,
                                  background: selected ? T.success : 'transparent',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  flexShrink: 0,
                                  color: '#FFFFFF', fontSize: 12, fontWeight: 700,
                                }}>
                                  {selected && '\u2713'}
                                </div>
                                <div style={{
                                  fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                                  color: selected ? T.success : T.text,
                                }}>
                                  {panel.name}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Assessment Input (pinned at bottom) ── */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: T.bgCard,
                  borderTop: `1px solid ${T.border}`,
                  padding: '10px 16px 16px',
                  boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
                  zIndex: 50,
                }}>
                  {/* Label + mode toggle */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 6,
                  }}>
                    <span style={{
                      fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                      color: T.info,
                    }}>
                      Assessment & Rationale
                    </span>
                    <button
                      onClick={() => setInputMode(prev => prev === 'type' ? 'voice' : 'type')}
                      style={{
                        padding: '2px 8px', borderRadius: T.radius.full,
                        border: `1px solid ${T.border}`, background: T.bgSoft,
                        fontFamily: F.body, fontSize: T.fontSize.sm,
                        color: T.textMuted, cursor: 'pointer',
                      }}
                    >
                      {inputMode === 'type' ? 'Voice' : 'Type'}
                    </button>
                  </div>

                  {inputMode === 'type' ? (
                    <textarea
                      value={diagnosis}
                      onChange={(e) => setDiagnosis(e.target.value)}
                      placeholder="Enter your blind diagnosis and rationale..."
                      rows={3}
                      style={{
                        width: '100%', padding: '8px 10px',
                        borderRadius: T.radius.sm,
                        border: `1px solid ${T.border}`,
                        background: T.bgInput,
                        fontFamily: F.body, fontSize: T.fontSize.sm,
                        color: T.text, resize: 'none',
                        outline: 'none', boxSizing: 'border-box',
                        lineHeight: 1.5,
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => setIsRecording(prev => !prev)}
                      style={{
                        width: '100%', padding: '12px 0',
                        borderRadius: T.radius.sm,
                        border: `1px solid ${isRecording ? T.danger : T.border}`,
                        background: isRecording ? T.dangerSoft : T.bgInput,
                        fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                        color: isRecording ? T.danger : T.textMuted,
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}
                    >
                      {isRecording ? (
                        <>
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: T.danger, animation: 'pulse-dot 1.5s ease infinite',
                            display: 'inline-block',
                          }} />
                          Recording... Tap to stop
                        </>
                      ) : (
                        'Tap to Record'
                      )}
                    </button>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    style={{
                      width: '100%', padding: '10px 0', marginTop: 8,
                      borderRadius: T.radius.sm,
                      background: canSubmit ? T.info : T.bgInput,
                      color: canSubmit ? '#FFFFFF' : T.textGhost,
                      fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                      border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Submit & Compare
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ============================================ */}
        {/* PHASE 2: REVEAL COMPARISON                   */}
        {/* ============================================ */}
        {phase === 'reveal' && (
          <div style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            padding: '16px 16px 40px',
          }}>
            {/* Your Assessment card */}
            <div style={{
              background: T.infoSoft,
              border: `1px solid rgba(37,99,235,0.15)`,
              borderRadius: T.radius.sm,
              padding: 14,
              marginBottom: 12,
            }}>
              <div style={{
                fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                color: T.info, letterSpacing: 1, textTransform: 'uppercase',
                marginBottom: 6,
              }}>
                YOUR ASSESSMENT
              </div>
              <div style={{
                fontFamily: F.body, fontSize: T.fontSize.sm,
                color: T.text, lineHeight: 1.5,
              }}>
                {diagnosis || '(No text entered \u2014 evidence-only submission)'}
              </div>
              {/* Summary of evidence */}
              {totalShelfItems > 0 && (
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8,
                }}>
                  {flaggedLabs.size > 0 && (
                    <span style={{
                      padding: '2px 6px', borderRadius: T.radius.full,
                      background: T.dangerSoft, color: T.danger,
                      fontSize: T.fontSize.sm, fontFamily: F.body,
                    }}>
                      {flaggedLabs.size} flagged labs
                    </span>
                  )}
                  {chatNotes.size > 0 && (
                    <span style={{
                      padding: '2px 6px', borderRadius: T.radius.full,
                      background: T.warningSoft, color: '#D97706',
                      fontSize: T.fontSize.sm, fontFamily: F.body,
                    }}>
                      {chatNotes.size} notes
                    </span>
                  )}
                  {selectedProducts.size > 0 && (
                    <span style={{
                      padding: '2px 6px', borderRadius: T.radius.full,
                      background: T.successSoft, color: T.success,
                      fontSize: T.fontSize.sm, fontFamily: F.body,
                    }}>
                      {selectedProducts.size} products
                    </span>
                  )}
                  {selectedLabOrders.size > 0 && (
                    <span style={{
                      padding: '2px 6px', borderRadius: T.radius.full,
                      background: T.successSoft, color: T.success,
                      fontSize: T.fontSize.sm, fontFamily: F.body,
                    }}>
                      {selectedLabOrders.size} lab orders
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* MedGemma 27B card */}
            <div style={{
              background: T.text,
              borderRadius: T.radius.sm,
              padding: 14,
              marginBottom: 12,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 8,
              }}>
                <div style={{
                  fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                  color: 'rgba(255,255,255,0.6)', letterSpacing: 1, textTransform: 'uppercase',
                }}>
                  MEDGEMMA 27B
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: T.radius.full,
                  background: 'rgba(255,255,255,0.15)',
                  color: '#FFFFFF', fontFamily: F.mono,
                  fontSize: T.fontSize.sm, fontWeight: 700,
                }}>
                  {MOCK_MEDGEMMA.grade}
                </span>
              </div>
              <div style={{
                fontFamily: F.mono, fontSize: T.fontSize.sm,
                color: 'rgba(255,255,255,0.85)', lineHeight: 1.6,
                marginBottom: 10,
              }}>
                {MOCK_MEDGEMMA.diagnosis}
              </div>
              <div style={{
                borderTop: '1px solid rgba(255,255,255,0.1)',
                paddingTop: 8,
              }}>
                <div style={{
                  fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)', letterSpacing: 1,
                  marginBottom: 4,
                }}>
                  PROTOCOL
                </div>
                <div style={{
                  fontFamily: F.mono, fontSize: T.fontSize.sm,
                  color: 'rgba(255,255,255,0.75)', lineHeight: 1.5,
                }}>
                  {MOCK_MEDGEMMA.protocol}
                </div>
              </div>
            </div>

            {/* Decision Points */}
            <div style={{
              fontFamily: F.body, fontSize: T.fontSize.md, fontWeight: 700,
              color: T.text, marginBottom: 10, marginTop: 8,
            }}>
              Decision Points
            </div>

            {MOCK_DECISION_POINTS.map((dp, i) => (
              <div
                key={i}
                style={{
                  background: T.bgCard,
                  borderRadius: T.radius.sm,
                  border: `1px solid ${T.border}`,
                  borderLeft: `3px solid ${alignmentColor(dp.alignment)}`,
                  padding: 12,
                  marginBottom: 8,
                }}
              >
                {/* Area + alignment badge */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 6,
                }}>
                  <span style={{
                    fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                    color: T.text,
                  }}>
                    {dp.area}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: T.radius.full,
                    background: alignmentBg(dp.alignment),
                    color: alignmentColor(dp.alignment),
                    fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
                  }}>
                    {alignmentLabel(dp.alignment)}
                  </span>
                </div>
                {/* MedGemma position */}
                <div style={{
                  fontFamily: F.body, fontSize: T.fontSize.sm,
                  color: T.textSoft, lineHeight: 1.4,
                }}>
                  {dp.medgemmaPosition}
                </div>
                {/* Delta note */}
                {dp.deltaNote && (
                  <div style={{
                    fontFamily: F.body, fontSize: T.fontSize.sm,
                    color: alignmentColor(dp.alignment),
                    fontStyle: 'italic', marginTop: 6,
                    lineHeight: 1.4,
                  }}>
                    {dp.deltaNote}
                  </div>
                )}
              </div>
            ))}

            {/* Fine-Tune button */}
            <button
              onClick={() => setPhase('tune')}
              style={{
                width: '100%', padding: '12px 0', marginTop: 12,
                borderRadius: T.radius.sm,
                background: T.accent, color: '#FFFFFF',
                fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 700,
                border: 'none', cursor: 'pointer',
              }}
            >
              Fine-Tune MedGemma &rarr;
            </button>
          </div>
        )}

        {/* ============================================ */}
        {/* PHASE 3: PROMPT MAKER                        */}
        {/* ============================================ */}
        {phase === 'tune' && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Scrollable messages area */}
            <div style={{
              flex: 1, overflowY: 'auto', overflowX: 'hidden',
              padding: '16px 16px 8px',
            }}>
              {/* Heading */}
              <div style={{
                fontFamily: F.body, fontSize: T.fontSize.lg, fontWeight: 700,
                color: T.text, marginBottom: 4,
              }}>
                Prompt Maker
              </div>
              <div style={{
                fontFamily: F.body, fontSize: T.fontSize.sm,
                color: T.textMuted, marginBottom: 16, lineHeight: 1.4,
              }}>
                Review the deltas and instruct MedGemma on how to handle similar cases in the future.
              </div>

              {/* Messages */}
              {tuneMessages.map((msg, i) => {
                if (msg.role === 'system') {
                  return (
                    <div
                      key={i}
                      style={{
                        background: T.bgSoft,
                        borderRadius: T.radius.sm,
                        padding: 12,
                        marginBottom: 10,
                        border: `1px solid ${T.border}`,
                      }}
                    >
                      <div style={{
                        fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                        color: T.textMuted, letterSpacing: 1,
                        textTransform: 'uppercase', marginBottom: 4,
                      }}>
                        SYSTEM
                      </div>
                      <div style={{
                        fontFamily: F.body, fontSize: T.fontSize.sm,
                        color: T.textSoft, lineHeight: 1.4,
                      }}>
                        {msg.text}
                      </div>
                    </div>
                  );
                }
                if (msg.role === 'ai') {
                  return (
                    <div
                      key={i}
                      style={{
                        background: T.accentSoft,
                        borderRadius: T.radius.sm,
                        padding: 12,
                        marginBottom: 10,
                        border: `1px solid ${T.accentGlow}`,
                      }}
                    >
                      <div style={{
                        fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                        color: T.accent, letterSpacing: 1,
                        textTransform: 'uppercase', marginBottom: 4,
                      }}>
                        MEDGEMMA
                      </div>
                      <div style={{
                        fontFamily: F.body, fontSize: T.fontSize.sm,
                        color: T.text, lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {msg.text}
                      </div>
                    </div>
                  );
                }
                // user
                return (
                  <div
                    key={i}
                    style={{
                      background: T.infoSoft,
                      borderRadius: T.radius.sm,
                      padding: 12,
                      marginBottom: 10,
                      border: `1px solid rgba(37,99,235,0.15)`,
                      marginLeft: 24,
                    }}
                  >
                    <div style={{
                      fontFamily: F.mono, fontSize: 10, fontWeight: 700,
                      color: T.info, letterSpacing: 1,
                      textTransform: 'uppercase', marginBottom: 4,
                    }}>
                      DR. C
                    </div>
                    <div style={{
                      fontFamily: F.body, fontSize: T.fontSize.sm,
                      color: T.text, lineHeight: 1.5,
                    }}>
                      {msg.text}
                    </div>
                  </div>
                );
              })}

              {/* Quick action chips */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 6,
                marginTop: 8, marginBottom: 12,
              }}>
                {QUICK_ACTIONS.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => setTuneInput(action)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: T.radius.full,
                      border: `1px solid ${T.border}`,
                      background: T.bgCard,
                      fontFamily: F.body, fontSize: T.fontSize.sm,
                      color: T.textSoft, cursor: 'pointer',
                    }}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>

            {/* Chat input (pinned at bottom) */}
            <div style={{
              flexShrink: 0,
              borderTop: `1px solid ${T.border}`,
              padding: '10px 16px 16px',
              background: T.bgCard,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <input
                type="text"
                value={tuneInput}
                onChange={(e) => setTuneInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTuneSend(); }}
                placeholder="Instruct MedGemma..."
                style={{
                  flex: 1, padding: '10px 12px',
                  borderRadius: T.radius.sm,
                  border: `1px solid ${T.border}`,
                  background: T.bgInput,
                  fontFamily: F.body, fontSize: T.fontSize.sm,
                  color: T.text, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={handleTuneSend}
                disabled={!tuneInput.trim()}
                style={{
                  width: 38, height: 38, borderRadius: T.radius.sm,
                  background: tuneInput.trim() ? T.accent : T.bgInput,
                  color: tuneInput.trim() ? '#FFFFFF' : T.textGhost,
                  border: 'none', cursor: tuneInput.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}
              >
                &#10148;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Click-away overlay for dropdown */}
      {caseOpen && (
        <div
          onClick={() => setCaseOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
          }}
        />
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>
    </ModuleShell>
  );
}
